// One-time upload of learning records kept in this browser by the legacy web app. The old page wrote
// jx_answer_manager_v1:owner:<user> and could leave unsynced states behind; after the cutover those
// answers must reach the server without asking the user to redo them. Non-destructive: the legacy keys
// stay in place, the marker only records that this browser uploaded them once.
const MARKER = 'jx_new_legacy_import_v1'
const KEY = 'jx_answer_manager_v1'
const MAX_BATCH = 25

const enc = (value: string) => value.replace(/[^A-Za-z0-9_-]/g, c => `_${c.charCodeAt(0).toString(16)}_`)
const ownerKey = (userId: string) => `${KEY}:owner:${encodeURIComponent(userId)}`
const markerKey = (userId: string) => `${MARKER}:${enc(userId)}`

interface LegacyState {
  questionId?: unknown
  stateUpdatedAt?: unknown
  revision?: unknown
  attempts?: unknown
  [key: string]: unknown
}

function readStates(userId: string): LegacyState[] {
  let raw = ''
  try { raw = localStorage.getItem(ownerKey(userId)) || localStorage.getItem(KEY) || '' } catch { return [] }
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw) as { questions?: Record<string, LegacyState> }
    return Object.values(parsed?.questions ?? {}).filter(state => {
      if (!state || typeof state !== 'object') return false
      const id = String(state.questionId ?? '')
      if (!/^(qv1-[0-9a-f]{8}|[A-Za-z0-9_-]{43})$/.test(id)) return false
      return Number.isFinite(Number(state.stateUpdatedAt)) && Number(state.attempts) > 0
    })
  } catch {
    return []
  }
}

async function eventIdFor(userId: string, state: LegacyState): Promise<string> {
  const text = JSON.stringify([userId, state.questionId, state.stateUpdatedAt, state.attempts, state.correct, state.mastered])
  try {
    const bytes = new TextEncoder().encode(text)
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    const hex = [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('')
    return `imp_${hex.slice(0, 40)}`
  } catch {
    return `imp_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`.slice(0, 60)
  }
}

async function upload(userId: string, states: LegacyState[]): Promise<boolean> {
  // The legacy state can be older than the 90-day event window; the event time is when we upload it.
  const clientTs = Date.now()
  const events = await Promise.all(states.map(async state => ({
    eventId: await eventIdFor(userId, state),
    type: 'answer-state', clientTs,
    payload: { question: { ...state, revision: Number(state.revision) || 1 } },
  })))
  const response = await fetch('/api/progress/events', {
    method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', 'X-Client-Surface': 'web', 'X-Client-Version': '2026.11.0' },
    body: JSON.stringify({ protocolVersion: 2, events }),
  })
  if (!response.ok) return false
  const body = await response.json().catch(() => null)
  return Boolean(body?.ok && Array.isArray(body.acceptedEventIds) && Array.isArray(body.duplicateEventIds))
}

/** Upload once per browser and account. Returns whether anything was imported. */
export async function importLegacyLearning(userId: string): Promise<boolean> {
  if (!userId || userId.startsWith('guest:')) return false
  try { if (localStorage.getItem(markerKey(userId))) return false } catch { return false }
  const states = readStates(userId)
  if (!states.length) {
    try { localStorage.setItem(markerKey(userId), JSON.stringify({ imported: 0, at: Date.now() })) } catch { /* private mode */ }
    return false
  }
  for (let index = 0; index < states.length; index += MAX_BATCH) {
    const ok = await upload(userId, states.slice(index, index + MAX_BATCH)).catch(() => false)
    if (!ok) return false
  }
  try { localStorage.setItem(markerKey(userId), JSON.stringify({ imported: states.length, at: Date.now() })) } catch { /* private mode */ }
  return true
}
